/**
 * Editorial-dark theme preset – cinematic, photography-forward, magazine-spread.
 *
 * Designed for orthopedic / sports-medicine / surgical / aesthetic / dental
 * providers who want a premium "magazine spread" feel. The fidelity bar is
 * "looks as good as the reference site" (https://nysportsandjoints.com) – not
 * "feels templated."
 *
 * Reference spec: docs/engineering/2026-05-07-editorial-dark-preset.md (§4)
 * Hard rules:    docs/engineering/2026-05-07-editorial-dark-charter.md
 *
 * Design pillars (Charter):
 * - Single saturated accent (mint #7DD4C0) on near-black surfaces.
 * - Two-tone H1 typography (typed spans, NOT markdown – see TwoToneHeading).
 * - Generous vertical rhythm + cinematic motion choreography.
 * - Photography earns every slot (`buildPhotoPrompt` provides cinematic prompts).
 *
 * AI-NOTE: This preset MUST conform to `ThemeSchema.parse({...})` at module
 * load time (acceptance test asserts). Any divergence from the schema field
 * names – e.g., proposing `motion.fadeInDuration` or `density.sectionPaddingY`
 * which don't exist on the schema – is a regression. The Charter and the
 * spec doc reference the actual fields by their `ThemeMotionSchema` /
 * `ThemeDensitySchema` / `ThemeHeroSchema` names.
 *
 * AI-NOTE: Self-hosted Manrope woff2 with preload (NOT Google CDN).
 * CDN-loaded fonts tank LCP and prevent the Lighthouse ≥ 95 gate from passing.
 * Mirrors the cosmetic preset's `cormorant-inter` pattern – exactly one
 * display weight is preloaded; everything else uses `font-display: optional`.
 *
 * AI-NOTE: `accentMuted`, `accentDim`, `border` rgba tonal variants are NOT
 * declared as theme tokens (would fail `HexColorSchema`). Encode at use site
 * via `color-mix(in srgb, var(--color-accent) 60%, transparent)`.
 */

import { ThemeSchema, type Theme } from '../types';

export const editorialDarkTheme: Theme = ThemeSchema.parse({
  name: 'editorial-dark',
  colors: {
    // Primary: mint accent (single saturated hue on dark)
    primary: '#7DD4C0',
    primaryDark: '#5DB5A1',
    primaryLight: '#A8E4D5',
    // Accent mirrors primary in this preset (single accent register)
    accent: '#7DD4C0',
    accentDark: '#5DB5A1',
    accentLight: '#A8E4D5',
    // Surfaces – warm near-black (NOT pure black)
    background: '#0A0B0E',
    backgroundAlt: '#15171C',
    surface: '#1A1C22',
    // Text
    text: '#F5F6F8',
    textMuted: '#9095A1',
    textInverse: '#0A0B0E',
    // Borders – strict hex; tonal variants encoded via color-mix() at use site
    border: '#1F2128',
    // Status (lightened for dark surface legibility)
    success: '#34D399',
    warning: '#F59E0B',
    error: '#F87171',
    // Medical-specific (mirrors cosmetic preset semantic palette)
    evidenceStrong: '#34D399',
    evidenceModerate: '#F59E0B',
    evidenceLow: '#9095A1',
    safetyWarning: '#F87171',
    safetyInfo: '#7DD4C0',
  },
  // P3 alternates omitted in V1 – sRGB only. Add when first provider needs them.
  typography: {
    // Manrope used for both display and body. Weight contrast (800 vs 400)
    // creates the editorial register without a second family.
    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    fontFamilyHeading: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    fontWeightHeading: '800',
    fontSizeBase: '1.0625rem', // 17px – slightly larger body for editorial register
    fontSizeSm: '0.875rem',
    fontSizeLg: '1.25rem',
    fontSizeXl: '1.5rem',
    fontSize2xl: '2rem',
    fontSize3xl: 'clamp(2.5rem, 5vw, 3.25rem)',
    fontSize4xl: 'clamp(3rem, 6vw, 4.5rem)',
    lineHeightBase: '1.6',
    lineHeightTight: '1.05',
    lineHeightLoose: '1.8',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem',
  },
  borderRadius: {
    none: '0',
    sm: '8px',
    md: '14px',
    lg: '20px',
    xl: '28px',
    full: '9999px',
  },
  // Dark-surface-tuned shadows; keep alpha low so they read as ambient lift,
  // not bright halos against the near-black background.
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.20)',
    md: '0 4px 8px -2px rgb(0 0 0 / 0.30), 0 2px 4px -1px rgb(0 0 0 / 0.20)',
    lg: '0 12px 24px -6px rgb(0 0 0 / 0.40), 0 4px 8px -2px rgb(0 0 0 / 0.25)',
    xl: '0 24px 48px -12px rgb(0 0 0 / 0.50), 0 8px 16px -4px rgb(0 0 0 / 0.30)',
  },
  layout: {
    heroStyle: 'image',
    cardStyle: 'minimal',
    iconStyle: 'geometric',
    buttonStyle: 'rounded',
    sectionPadding: 'clamp(4rem, 10vw, 9rem)',
    contentMaxWidth: '72rem',
  },
  // Motion tokens – mirror the cosmetic motion charter shape so the global
  // reduced-motion reset emitted by `themeToCss()` continues to work.
  // Editorial-dark uses the SAME numeric values as cosmetic for shared tiers
  // (durationFast / durationNormal / durationModerate / durationEditorial)
  // because the cinematic register's hero entrance duration is the same
  // 600ms long-tail used in the Consona pattern.
  motion: {
    durationInstant: '0ms',
    durationFast: '150ms',
    durationNormal: '200ms',
    durationModerate: '250ms',
    durationEditorial: '600ms',
    easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
    easeIn: 'cubic-bezier(0.55, 0, 1, 0.45)',
    easeInOut: 'cubic-bezier(0.45, 0, 0.55, 1)',
    easeEditorial: 'cubic-bezier(0.22, 1, 0.36, 1)',
    distanceShort: '8px',
    distanceMedium: '16px',
    distanceLong: '32px',
    parallaxMax: '8%',
  },
  // Density tokens – generous vertical rhythm; homepage may be 7-11 sections
  // (vs cosmetic's 4-6) because editorial-dark reads as a magazine spread.
  density: {
    homeSectionGap: '4rem',
    homeSectionGapDesktop: '9rem',
    homeMaxSections: 11,
    contentMaxWidthDisplay: '22ch',
    contentMaxWidthLede: '48ch',
    contentMaxWidthProse: '68ch',
    contentMaxWidthEditorial: '72rem',
    contentMaxWidthFullBleed: '100vw',
    gutterMobile: '1.5rem',
    gutterDesktop: '4rem',
  },
  // Hero CSS hooks for the cinematic full-bleed variant.
  // Overlay scrim is a vertical gradient that darkens the bottom of the photo
  // so bottom-anchored hero text stays legible regardless of source image.
  hero: {
    aspectMobile: '4 / 5',
    aspectDesktop: '16 / 9',
    markOrigin: 'top-left',
    textOrigin: 'bottom-left',
    overlayScrim: 'linear-gradient(180deg, rgba(10, 11, 14, 0.20) 0%, rgba(10, 11, 14, 0.85) 100%)',
    imageFadeDuration: '400ms',
  },
  // Named font pairings (B2 library shape). The launch pairing is
  // `manrope-only` – Manrope handles both display and body weight tiers.
  //
  // Font files are self-hosted under `/fonts/editorial-dark/manrope/...`
  // (path mirrors the cosmetic preset). Per the Performance Budget shape:
  // exactly one display weight is preloaded on first paint; everything else
  // uses `font-display: optional`.
  fontPairings: {
    'manrope-only': {
      id: 'manrope-only',
      label: 'Manrope (display + body)',
      default: true,
      fontFamilyDisplay: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontFamilyHeading: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontFamilyBody: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontFamilyUi: 'Manrope, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
      fontWeightDisplay: '800',
      fontWeightHeading: '800',
      fontWeightBody: '400',
      fontWeightUi: '600',
      trackingDisplay: '-0.02em',
      trackingHeading: '-0.01em',
      trackingBody: '0',
      trackingLabel: '0.16em',
      trackingWordmark: '0.18em',
      files: [
        // Preloaded: hero display weight (Manrope 800).
        {
          family: 'Manrope',
          weight: '800',
          style: 'normal',
          url: '/fonts/editorial-dark/manrope/manrope-800.woff2',
          format: 'woff2',
          preload: true,
          display: 'optional',
        },
        // Body regular (Manrope 400).
        {
          family: 'Manrope',
          weight: '400',
          style: 'normal',
          url: '/fonts/editorial-dark/manrope/manrope-400.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
        // UI medium (kicker / label tier).
        {
          family: 'Manrope',
          weight: '500',
          style: 'normal',
          url: '/fonts/editorial-dark/manrope/manrope-500.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
        // UI semibold (CTA buttons, two-tone accent spans).
        {
          family: 'Manrope',
          weight: '600',
          style: 'normal',
          url: '/fonts/editorial-dark/manrope/manrope-600.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
        // Heading bold (for H2/H3 where 800 is too heavy).
        {
          family: 'Manrope',
          weight: '700',
          style: 'normal',
          url: '/fonts/editorial-dark/manrope/manrope-700.woff2',
          format: 'woff2',
          preload: false,
          display: 'optional',
        },
      ],
    },
  },
});
